import { useFetchUnifiedFields } from '@modules/ManageUnifiedFields/hooks';
import { CommonFieldDetailsTooltipContent } from '@modules/common/components/CommonFieldDetailsTooltipContent';
import { EqlFieldCommonMetadataSourceIcons } from '@modules/common/components/EqlFieldCommonMetadataInfo';
import { UnifiedFieldIcon } from '@modules/common/components/UnifiedFieldIcon';
import { CheckCircleOutlineRounded, CloseRounded } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  IconButton,
  ListItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

interface SelectSourceMetadataFieldForCohortGroupProps {
  value: string;
  onChange: (value: string) => void;
}

export const SelectUnifiedMetadataFieldForCohortGroup = ({
  value,
  onChange,
}: SelectSourceMetadataFieldForCohortGroupProps) => {
  const { unifiedFields, loading } = useFetchUnifiedFields({
    shouldFetchValues: false,
  });

  const selectedMetadataField = unifiedFields?.find(
    (unfiedField) => unfiedField.eqlField.field === value,
  );

  const isDisabled = !!value && !selectedMetadataField;

  return (
    <Stack
      flexDirection="row"
      justifyContent="space-between"
      my={1}
      spacing={0.5}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          opacity: isDisabled ? 0.5 : 1,
        }}
      >
        <UnifiedFieldIcon
          sx={{
            width: '30px !important',
            height: '22px !important',
          }}
        />
        <Typography variant="body2">Unified Field</Typography>
        {!!selectedMetadataField && (
          <CheckCircleOutlineRounded
            sx={{
              color: 'success.light',
            }}
          />
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box width={350}>
          <Autocomplete
            loading={loading}
            disabled={isDisabled}
            size="small"
            options={unifiedFields ?? []}
            getOptionLabel={(option) => option.eqlField.displayName}
            value={selectedMetadataField || null}
            onChange={(_, newValue) => {
              onChange(newValue?.eqlField.field || '');
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select metadata field" />
            )}
            isOptionEqualToValue={(option, value) =>
              option.eqlField.field === value.eqlField.field
            }
            renderOption={(props, option) => {
              return (
                <ListItem
                  {...props}
                  key={option.eqlField.field}
                  sx={{ width: '100%' }}
                >
                  <Tooltip
                    arrow
                    placement="right"
                    disableInteractive
                    title={
                      <CommonFieldDetailsTooltipContent
                        field={option.eqlField}
                      />
                    }
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                    >
                      <Typography variant="body1">
                        {option.eqlField.displayName}
                      </Typography>
                      <EqlFieldCommonMetadataSourceIcons
                        eqlField={option.eqlField}
                        shouldHideTooltip
                      />
                    </Stack>
                  </Tooltip>
                </ListItem>
              );
            }}
            clearIcon={null}
          />
        </Box>
        <Box width={40}>
          {!!selectedMetadataField && (
            <IconButton size="small" onClick={() => onChange('')}>
              <CloseRounded />
            </IconButton>
          )}
        </Box>
      </Stack>
    </Stack>
  );
};
